import { initializeApp } from "firebase/app";
import { getAuth, setPersistence, browserLocalPersistence } from "firebase/auth";
import { getFirestore, enableIndexedDbPersistence } from "firebase/firestore";
import { getStorage } from "firebase/storage";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
setPersistence(auth, browserLocalPersistence).catch(() => {});

const db = getFirestore(app);
const storage = getStorage(app);

try {
  enableIndexedDbPersistence(db);
} catch (e) {
  // ignore persistence errors
}

// VAPID public key for web push
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

// Convert VAPID key from base64url to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Subscribe to web push notifications
export async function subscribeToPush() {
  console.log('[PUSH] Starting subscription process...');
  
  if (!('serviceWorker' in navigator)) {
    console.warn('[PUSH] Service Worker not supported');
    return null;
  }
  
  if (!('PushManager' in window)) {
    console.warn('[PUSH] PushManager not supported');
    return null;
  }
  
  if (!VAPID_PUBLIC_KEY) {
    console.error('[PUSH] VAPID_PUBLIC_KEY not configured');
    return null;
  }
  
  console.log('[PUSH] VAPID_PUBLIC_KEY configured:', VAPID_PUBLIC_KEY.substring(0, 20) + '...');
  
  try {
    // Request notification permission
    console.log('[PUSH] Requesting permission...');
    const permission = await Notification.requestPermission();
    console.log('[PUSH] Permission result:', permission);
    
    if (permission !== 'granted') {
      console.log('[PUSH] Permission denied');
      return null;
    }
    
    // Get service worker registration
    console.log('[PUSH] Getting service worker registration...');
    const registration = await navigator.serviceWorker.ready;
    console.log('[PUSH] Service worker ready:', registration.scope);
    
    // Check if already subscribed
    const existingSub = await registration.pushManager.getSubscription();
    if (existingSub) {
      console.log('[PUSH] Existing subscription found, returning it');
      const subData = existingSub.toJSON();
      return {
        endpoint: subData.endpoint,
        keys: {
          p256dh: subData.keys.p256dh,
          auth: subData.keys.auth
        }
      };
    }
    
    // Subscribe to push
    console.log('[PUSH] Creating new subscription...');
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
    });
    
    console.log('[PUSH] Subscription obtained successfully');
    
    // Return subscription data in the format needed by backend
    const subscriptionData = subscription.toJSON();
    
    if (!subscriptionData.endpoint || !subscriptionData.keys?.p256dh || !subscriptionData.keys?.auth) {
      console.error('[PUSH] Subscription data incomplete:', subscriptionData);
      return null;
    }
    
    console.log('[PUSH] Subscription endpoint:', subscriptionData.endpoint.substring(0, 50) + '...');
    
    return {
      endpoint: subscriptionData.endpoint,
      keys: {
        p256dh: subscriptionData.keys.p256dh,
        auth: subscriptionData.keys.auth
      }
    };
  } catch (error) {
    console.error('[PUSH] Error subscribing:', error.name, error.message);
    return null;
  }
}

// Check if already subscribed
export async function getExistingSubscription() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      const subscriptionData = subscription.toJSON();
      return {
        endpoint: subscriptionData.endpoint,
        keys: {
          p256dh: subscriptionData.keys.p256dh,
          auth: subscriptionData.keys.auth
        }
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting existing subscription:', error);
    return null;
  }
}

// Unsubscribe from push
export async function unsubscribeFromPush() {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      await subscription.unsubscribe();
      console.log('Unsubscribed from push notifications');
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error unsubscribing from push:', error);
    return false;
  }
}

export { app, auth, db, storage };
